import { useState } from "react";
import { Phone, Mail, MapPin, Send, CheckCircle } from "lucide-react";
import axios from "axios";
import { usePageMeta } from "@/hooks/usePageMeta";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceOptions = [
  "IT Services",
  "ERP Services",
  "Cloud Services",
  "IT Infrastructure",
  "Cyber Security",
  "Security & Surveillance",
  "Other",
];

export default function ContactPage() {
  usePageMeta("Contact Us", "Get in touch with Vaana BuildLogic Technologies. Phone: +91 9953167073. Office: Near Badarpur Metro Station, New Delhi 110044.");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service_interest: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      await axios.post(`${API}/contact`, form);
      setSuccess(true);
      setForm({
        name: "",
        email: "",
        phone: "",
        company: "",
        service_interest: "",
        message: "",
      });
    } catch (err) {
      setError("Something went wrong. Please try again or contact us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-20">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-[#0A122A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <p className="text-xs tracking-[0.2em] uppercase text-[#00E5FF] font-semibold mb-4">
            Contact Us
          </p>
          <h1 className="text-5xl sm:text-6xl tracking-tight leading-none font-['Outfit'] font-bold text-white mb-6">
            Get In Touch
          </h1>
          <p className="text-[#94A3B8] max-w-2xl leading-relaxed">
            Have a project in mind or need a technology consultation? Reach out to us and our team will get back to you within 24 hours.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h3 className="text-xl font-['Outfit'] font-semibold text-white mb-6">
                  Contact Information
                </h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#111D3D] rounded-md flex-shrink-0">
                      <Phone size={18} className="text-[#00E5FF]" />
                    </div>
                    <div>
                      <p className="text-sm text-[#64748B] mb-1">Phone</p>
                      <a
                        href="tel:9953167073"
                        data-testid="contact-phone"
                        className="text-white hover:text-[#00E5FF] transition-colors"
                      >
                        +91 9953167073
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#111D3D] rounded-md flex-shrink-0">
                      <Mail size={18} className="text-[#00E5FF]" />
                    </div>
                    <div>
                      <p className="text-sm text-[#64748B] mb-1">Email</p>
                      <a
                        href="mailto:info@vaanabuildlogictechnologies.com"
                        data-testid="contact-email"
                        className="text-white hover:text-[#00E5FF] transition-colors break-all"
                      >
                        info@vaanabuildlogictechnologies.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#111D3D] rounded-md flex-shrink-0">
                      <MapPin size={18} className="text-[#00E5FF]" />
                    </div>
                    <div>
                      <p className="text-sm text-[#64748B] mb-1">Address</p>
                      <p data-testid="contact-address" className="text-white">
                        Office No. 7 Ground Floor<br />
                        Indra Nursery, Near Badarpur Metro Station<br />
                        New Delhi 110044
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Business Hours */}
              <div className="p-6 bg-[#0A122A] border border-white/8 rounded-md">
                <h4 className="text-sm font-semibold text-white mb-3 font-['Outfit']">Business Hours</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#94A3B8]">Mon - Fri</span>
                    <span className="text-white">9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#94A3B8]">Saturday</span>
                    <span className="text-white">10:00 AM - 4:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#94A3B8]">Sunday</span>
                    <span className="text-[#64748B]">Closed</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Enquiry Form */}
            <div className="lg:col-span-3">
              <div className="p-8 md:p-10 bg-[#0A122A] border border-white/8 rounded-md">
                <h3 className="text-xl font-['Outfit'] font-semibold text-white mb-2">
                  Send Us an Enquiry
                </h3>
                <p className="text-sm text-[#94A3B8] mb-8">
                  Fill out the form below and we will respond within 24 hours.
                </p>

                {success && (
                  <div data-testid="contact-success-message" className="mb-6 p-4 bg-[#00E5FF]/10 border border-[#00E5FF]/30 rounded-md flex items-center gap-3">
                    <CheckCircle size={18} className="text-[#00E5FF]" />
                    <p className="text-sm text-[#00E5FF]">
                      Thank you! Your enquiry has been submitted successfully. We will get back to you soon.
                    </p>
                  </div>
                )}

                {error && (
                  <div data-testid="contact-error-message" className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-md">
                    <p className="text-sm text-red-400">{error}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="text-xs text-[#94A3B8] mb-1.5 block">Full Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        required
                        data-testid="contact-input-name"
                        className="w-full px-4 py-3 bg-[#060B19] border border-white/10 rounded-md text-white text-sm placeholder-[#64748B] focus:outline-none focus:border-[#00E5FF]/50 focus:ring-1 focus:ring-[#00E5FF]/30 transition-all"
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#94A3B8] mb-1.5 block">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        required
                        data-testid="contact-input-email"
                        className="w-full px-4 py-3 bg-[#060B19] border border-white/10 rounded-md text-white text-sm placeholder-[#64748B] focus:outline-none focus:border-[#00E5FF]/50 focus:ring-1 focus:ring-[#00E5FF]/30 transition-all"
                        placeholder="john@company.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="text-xs text-[#94A3B8] mb-1.5 block">Phone Number</label>
                      <input
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        data-testid="contact-input-phone"
                        className="w-full px-4 py-3 bg-[#060B19] border border-white/10 rounded-md text-white text-sm placeholder-[#64748B] focus:outline-none focus:border-[#00E5FF]/50 focus:ring-1 focus:ring-[#00E5FF]/30 transition-all"
                        placeholder="+91 XXXXXXXXXX"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#94A3B8] mb-1.5 block">Company Name</label>
                      <input
                        type="text"
                        name="company"
                        value={form.company}
                        onChange={handleChange}
                        data-testid="contact-input-company"
                        className="w-full px-4 py-3 bg-[#060B19] border border-white/10 rounded-md text-white text-sm placeholder-[#64748B] focus:outline-none focus:border-[#00E5FF]/50 focus:ring-1 focus:ring-[#00E5FF]/30 transition-all"
                        placeholder="Your Company"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs text-[#94A3B8] mb-1.5 block">Service Interest</label>
                    <select
                      name="service_interest"
                      value={form.service_interest}
                      onChange={handleChange}
                      data-testid="contact-select-service"
                      className="w-full px-4 py-3 bg-[#060B19] border border-white/10 rounded-md text-white text-sm focus:outline-none focus:border-[#00E5FF]/50 focus:ring-1 focus:ring-[#00E5FF]/30 transition-all appearance-none"
                    >
                      <option value="" className="bg-[#060B19]">Select a service</option>
                      {serviceOptions.map((opt) => (
                        <option key={opt} value={opt} className="bg-[#060B19]">
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-xs text-[#94A3B8] mb-1.5 block">Message *</label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      data-testid="contact-input-message"
                      className="w-full px-4 py-3 bg-[#060B19] border border-white/10 rounded-md text-white text-sm placeholder-[#64748B] focus:outline-none focus:border-[#00E5FF]/50 focus:ring-1 focus:ring-[#00E5FF]/30 transition-all resize-none"
                      placeholder="Tell us about your project or requirements..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit-button"
                    className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#00E5FF] text-[#060B19] font-semibold text-sm rounded-sm hover:bg-[#33EEFF] transition-all duration-300 btn-glow disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? "Submitting..." : "Submit Enquiry"}
                    <Send size={16} />
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
